import { Card, CardContent, Link, TableCell, Typography } from "@mui/material";
import type { Column } from "~/components/Table";
import {
  getActiveFiltersCount,
  makeRequestSchema,
  prepareListRequest,
  ResourceTable,
  useSearchRequest,
  withoutBaseTableModel,
  withSearchValidationErrorBoundary,
} from "~/components/Table";
import { auditColumns } from "~/domain/auditable";
import { useApiKeys } from "~/domain/datastores";
import {
  DataStoreLink,
  makeApiKeyLocation,
  makeNewApiKeyLocation,
} from "~/paths";
import type { APIKey } from "~/services/datastore";
import { listApiKeysSchema } from "../schemas";
import { ApiKeyFilters } from "./ApiKeyFilters";

const COLUMNS: ReadonlyArray<Column<APIKey>> = [
  {
    header: "Name",
    renderCell(apiKey) {
      return (
        <TableCell>
          {apiKey.name}
          <Link
            component={DataStoreLink}
            to={makeApiKeyLocation({ apiKeyId: apiKey.id })}
          >
            <Typography variant="body2">{apiKey.id}</Typography>
          </Link>
        </TableCell>
      );
    },
  },
  {
    accessor: "userId",
    dataType: "foreign-key",
    resourceType: "user",
  },
  {
    accessor: "disabled",
    dataType: "boolean",
    isSortable: true,
    defaultHidden: true,
  },
  ...auditColumns,
];

const requestSchema = makeRequestSchema(COLUMNS, listApiKeysSchema);

export const ApiKeyTable = withSearchValidationErrorBoundary(
  function ApiKeyTable() {
    const [request, setRequest] = useSearchRequest(requestSchema);

    const searchQuery = useApiKeys(prepareListRequest(request), {
      keepPreviousData: true,
      cacheTime: 0,
    });

    const filterValues = withoutBaseTableModel(request);

    return (
      <Card>
        <CardContent>
          <ResourceTable
            resourceName="API Key"
            resourceCreateLocation={makeNewApiKeyLocation()}
            getRowKey={(apiKey) => apiKey.id}
            columns={COLUMNS}
            searchQuery={searchQuery}
            tableModel={request}
            onTableModelChange={setRequest}
            filterSection={
              <ApiKeyFilters values={filterValues} setValues={setRequest} />
            }
            activeFilterCount={getActiveFiltersCount(filterValues)}
          />
        </CardContent>
      </Card>
    );
  },
);
